import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import useIsMobile from "../hooks/useIsMobile";
import Topbar from "./Topbar";
import { BOARD_STAGE_LABELS, moneyK, sourcePill, contactName, activityLabel, returningTitle, moneyMeaningTitle, dateShort } from "./ui";
import StageLine from "./StageLine";
import WaitingBar from "./WaitingBar";
import { waitingAction } from "./waiting.mjs";
import { holdState, SNOOZE_LABELS } from "./hold.mjs";
import { isHot, HOT_TITLE } from "./hot.mjs";
import { silenceTier, silenceTitle } from "./silence.mjs";
import AuditNotes, { auditTitle } from "./AuditNotes";
import { leadScoreTitle } from "./leadScore";
import ActionChip from "./ActionChip";
import { callActionsOf, itemsLabel, itemsTitle } from "./callActions.mjs";
import Flag from "./flags";
import DateField from "./datepicker/DateField";
import { todayYmd } from "./datepicker/calendar.mjs";

const OPEN_STAGES = ["new", "call_scheduled", "in_progress", "proposal_sent", "evaluating", "agreement"];
// Every destination a card can be moved to by hand, in board order. Drag-and-drop
// is the desktop way in; on a phone this is the list a long-press opens.
const ALL_STAGES = [...OPEN_STAGES, "closed_won", "closed_lost"];
const LONG_PRESS_MS = 450;
// Disqualify is for people he never wants to talk to (2026-09-30). No budget or
// misaligned is "Remove from pipeline": they might have budget again some day.
const DISQUALIFY_REASONS = [["junk", "Junk"], ["bad_fit", "Bad fit"]];

// "Remove from pipeline…" = out, not dead: the stage resets, the history stays,
// and they come back if they come back. The wake-up is a reminder only, and
// "No reminder" is one click. Intervals are the common case; the custom date
// covers "their budget opens in February".
const RECYCLE_INTERVALS = [3, 6, 12];
const RECYCLE_REASONS = [
  ["stopped_chasing", "Stopped chasing"], ["ghosted", "Ghosted"], ["no_budget_now", "No budget now"],
  ["timing", "Timing"], ["browsing", "Browsing"],
];
const RECYCLE_DEFAULT = { months: 6, date: "", none: false, reason: "stopped_chasing" };

/*
 * "HOLD…" = a snooze, set from the card.
 *
 * Recycle and hold are both "not now" and they are not the same decision. A recycle
 * changes the company's STATUS and takes it off the board until an inbound signal
 * brings it back (docs/CONVENTIONS.md — a company re-enters the pipeline on their
 * signal, never on our date). A hold changes nothing about the record: the card keeps
 * its column, its stage and its money, and comes back on the day Gabriel named. So
 * "they said reconnect in a couple of weeks" is a hold, and "their budget opens in
 * February" is still a recycle.
 *
 * The board could not set one until now — the snooze menu lives on a Follow-ups row,
 * which only exists while a rule is matching the company — so a deal mid-agreement
 * that asked for two weeks had nowhere to say so.
 *
 * The window is resolved SERVER-side from the preset name (see the endpoint's own
 * note: two clients computing "next week" off their own clocks is how one row ends up
 * promised for a Sunday). A hand-picked date is sent as a date, which is what the
 * endpoint expects for that case.
 */
const HOLD_DEFAULT = { preset: "2_weeks", date: "", note: "" };

function monthsFromToday(months) {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

export default function BoardPage() {
  const [board, setBoard] = useState(null);
  const [error, setError] = useState("");
  const [dragId, setDragId] = useState(null);
  const [dropStage, setDropStage] = useState(null);
  const [menuFor, setMenuFor] = useState(null);
  // Which panel the anchored ⋯ popover is showing: the disqualify reason picker
  // (the default, per the design notes) or the recycle picker.
  const [menuMode, setMenuMode] = useState("disqualify");
  const [reason, setReason] = useState({ value: "junk", note: "" });
  const [recycle, setRecycle] = useState(RECYCLE_DEFAULT);
  const [holdForm, setHoldForm] = useState(HOLD_DEFAULT);
  // Hide stale + ghosted cards. Persisted in settings, not component state, so
  // a quieted board stays quiet across reloads.
  const [hideStale, setHideStale] = useState(false);
  /*
   * Which columns have their dormant cards shown. PER SESSION, not persisted, and
   * that is the point: a dormant card is hidden because it is not today's work,
   * so revealing it is a thing you do once to look, not a preference. hideStale
   * above is the opposite — a standing choice — which is why it lives in settings
   * and this lives in a useState.
   */
  const [showDormant, setShowDormant] = useState({});
  // Audited only: the board narrowed to the companies whose LinkedIn account we
  // have audited. Per session like showDormant — it is a look, not a preference.
  const [auditedOnly, setAuditedOnly] = useState(false);
  // 🔥 Hot only: the board narrowed to what is in play right now (./hot.mjs). Per
  // session like auditedOnly, and a button of its own rather than a line in the
  // gear, because it is the look Gabriel reaches for, not a setting.
  const [hotOnly, setHotOnly] = useState(false);
  // The lead's country flag beside the company name. A standing choice like
  // hideStale, so it lives in settings.
  const [showFlags, setShowFlags] = useState(false);
  // The intake's lead score tag on the card. On unless switched off in the gear.
  const [showScore, setShowScore] = useState(true);
  // The gear in the header: the board's view filters live behind it rather than
  // as a row of toggle buttons.
  const [gearOpen, setGearOpen] = useState(false);
  const gearRef = useRef(null);
  // Columns Gabriel has put away. Persisted, because a board you re-tidy on every
  // page load isn't tidied.
  const [hidden, setHidden] = useState([]);
  // Per-card intelligence, keyed by company id. Same module as Today and
  // Follow-ups, fetched separately so the board query stays one concern.
  const [chips, setChips] = useState({});
  // Served alongside them (GET /board-chips), and the only source of the fade's cuts.
  const [thresholds, setThresholds] = useState(null);
  // Phone only: the board becomes one column per screen, so it needs to say
  // which column you are on, and a card needs a way to be moved that isn't
  // dragging (there is no drag on touch).
  const isMobile = useIsMobile();
  const [page, setPage] = useState(0);
  const [moveFor, setMoveFor] = useState(null); // { company, stage }
  const boardRef = useRef(null);
  const pressTimer = useRef(null);
  const pressFired = useRef(false);
  const navigate = useNavigate();

  // The gone tier's own way in, straight to the two answers. Not a toggle like the ⋯
  // menu: the button says what it opens, so clicking it twice closing the panel would
  // be a surprise.
  function openRevive(companyId) {
    setHoldForm(HOLD_DEFAULT);
    setMenuMode("revive");
    setMenuFor(companyId);
  }

  function openMenu(companyId) {
    setReason({ value: "junk", note: "" });
    setRecycle(RECYCLE_DEFAULT);
    setHoldForm(HOLD_DEFAULT);
    setMenuMode("disqualify");
    setMenuFor(menuFor === companyId ? null : companyId);
  }

  const load = useCallback(async () => {
    try {
      const [b, settings] = await Promise.all([api("/board"), api("/settings")]);
      setBoard(b);
      setHideStale(settings.board_hide_stale === true);
      setShowFlags(settings.board_show_flags === true);
      setShowScore(settings.board_show_score !== false);
      setHidden(Array.isArray(settings.board_hidden_columns) ? settings.board_hidden_columns : []);
      // A chip failure must not take the board down: the cards stand on their own
      // facts, and the stage lines are an addition to them.
      // The thresholds come back with the chips and are kept, not dropped: the fade's
      // four tiers are cut at stale_days and ghost_days, and reading them off the
      // response is what stops the board holding a second copy of two numbers that
      // are tuned in settings.
      api("/board-chips")
        .then((c) => { setChips(c.chips || {}); setThresholds(c.thresholds || null); })
        .catch(() => { setChips({}); setThresholds(null); });
    } catch (e) { setError(e.message); }
  }, []);

  // Optimistic: the board is already loaded, so flipping this is instant and the
  // PUT only records the preference.
  async function toggleHideStale() {
    const next = !hideStale;
    setHideStale(next);
    try {
      await api("/settings", { method: "PUT", body: JSON.stringify({ board_hide_stale: next }) });
    } catch (e) { setError(e.message); setHideStale(!next); }
  }
  async function toggleShowFlags() {
    const next = !showFlags;
    setShowFlags(next);
    try {
      await api("/settings", { method: "PUT", body: JSON.stringify({ board_show_flags: next }) });
    } catch (e) { setError(e.message); setShowFlags(!next); }
  }
  async function toggleShowScore() {
    const next = !showScore;
    setShowScore(next);
    try {
      await api("/settings", { method: "PUT", body: JSON.stringify({ board_show_score: next }) });
    } catch (e) { setError(e.message); setShowScore(!next); }
  }
  useEffect(() => { load(); }, [load]);
  // A click anywhere outside the gear's anchor puts the panel away.
  useEffect(() => {
    if (!gearOpen) return undefined;
    const onDown = (e) => { if (gearRef.current && !gearRef.current.contains(e.target)) setGearOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [gearOpen]);
  useEffect(() => () => clearTimeout(pressTimer.current), []);

  // A manual pipeline/status move (manual moves may go any direction). The PATCH
  // writes a stage_transition server-side. Two ways in — dropping a dragged card
  // on desktop, picking a stage from the long-press sheet on a phone — and one
  // write, so both produce the same transition.
  async function moveCompany(id, stage) {
    if (!id) return;
    const body =
      stage === "closed_won" ? { status: "client" } :
      stage === "closed_lost" ? { status: "lost" } :
      { status: "prospect", pipeline_stage: stage };
    try {
      await api(`/companies/${id}`, { method: "PATCH", body: JSON.stringify(body) });
      await load();
    } catch (e) { setError(e.message); }
  }

  async function onDrop(stage) {
    const id = dragId;
    setDragId(null); setDropStage(null);
    await moveCompany(id, stage);
  }

  // Long-press = the touch stand-in for dragging. Any movement or lift before
  // the timer cancels it, so scrolling the column never opens the sheet.
  function startPress(company, stage) {
    if (!isMobile) return;
    pressFired.current = false;
    clearTimeout(pressTimer.current);
    pressTimer.current = setTimeout(() => {
      pressFired.current = true;
      setMoveFor({ company, stage });
      if (navigator.vibrate) navigator.vibrate(8);
    }, LONG_PRESS_MS);
  }
  function endPress() { clearTimeout(pressTimer.current); }

  // Where a card goes when you open it: the POC's page, which is where the
  // timeline, the action bar and the composer live. /companies/:id has no route.
  // Two callers — the card body and the waiting footer's verb — so they cannot
  // land in different places.
  function openCompany(co) {
    if (!co.primary_contact_id) return;
    navigate(`/contacts/${co.primary_contact_id}`, { state: { from: { to: "/", label: "Pipeline" } } });
  }

  // Which column is on screen. Read from geometry rather than tracked as an
  // index, because the scroller is the source of truth — a flung swipe can land
  // anywhere and the dots must agree with what is actually showing.
  function onBoardScroll() {
    const el = boardRef.current;
    if (!el || !isMobile) return;
    const left = el.getBoundingClientRect().left;
    let best = 0, bestD = Infinity;
    Array.from(el.children).forEach((col, i) => {
      const d = Math.abs(col.getBoundingClientRect().left - left);
      if (d < bestD) { bestD = d; best = i; }
    });
    setPage((p) => (p === best ? p : best));
  }

  function goToPage(i) {
    const el = boardRef.current;
    if (!el || !el.children[i]) return;
    el.children[i].scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
  }

  async function disqualify(company) {
    const r = reason.value;
    const note = reason.note.trim();
    setMenuFor(null);
    try {
      await api(`/companies/${company.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: "not_a_fit", status_reason: r }),
      });
      // status_reason has no room for prose, so an optional note lands on the
      // timeline as a note activity instead of being silently dropped.
      if (note) {
        await api("/activities", {
          method: "POST",
          body: JSON.stringify({
            type: "note", company_id: company.id,
            title: `Disqualified · ${r.replace(/_/g, " ")}`, body: note,
          }),
        });
      }
      if (r === "junk" && company.primary_contact_id) {
        await api(`/contacts/${company.primary_contact_id}/junk`, { method: "POST" });
      }
      await load();
    } catch (e) { setError(e.message); }
  }

  // Take a company out of the pipeline. One PATCH carries status + wake-up date +
  // reason together (recycle_until is part of the pipeline write, never a side
  // update). It leaves the board and comes back only if they write.
  async function recycleCompany(company) {
    const until = recycle.none ? null : recycle.date || monthsFromToday(recycle.months);
    setMenuFor(null);
    try {
      await api(`/companies/${company.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: "recycled", recycle_until: until, status_reason: recycle.reason }),
      });
      await load();
    } catch (e) { setError(e.message); }
  }

  /*
   * Put the company on hold. Nothing about the RECORD changes — this writes to
   * follow_up_state, which is why the card stays exactly where it is and only stops
   * being offered as work.
   *
   * A hand-picked date wins over the preset chips, the same way it does in the recycle
   * panel. `load()` refetches the chips as well as the board, and the hold is read off
   * the chips, so the hatch appears on the same round trip.
   */
  async function holdCompany(company) {
    const body = holdForm.date
      ? { until: holdForm.date, note: holdForm.note.trim() || null }
      : { preset: holdForm.preset, note: holdForm.note.trim() || null };
    setMenuFor(null);
    try {
      await api(`/follow-ups/${company.id}/snooze`, { method: "POST", body: JSON.stringify(body) });
      await load();
    } catch (e) { setError(e.message); }
  }

  /*
   * REMOVE A GHOST FROM THE PIPELINE — the "stop" half of the last tier's question.
   *
   * Writes exactly what the weekly sweep proposes for the same situation: recycled,
   * status_reason 'ghosted', a six-month wake-up. Ghosting is never lost and never
   * disqualified (Gabriel, 2026-09-30); it is a lead out of the pipeline. Doing it by
   * hand the same way matters, because the reason is what the reports and the lead
   * agent read afterwards.
   *
   * Nothing is deleted — the timeline, the money and the history stay, and rule 14
   * brings the company back if they write.
   */
  async function closeOut(company) {
    setMenuFor(null);
    try {
      await api(`/companies/${company.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: "recycled", status_reason: "ghosted", recycle_until: monthsFromToday(6) }),
      });
      await load();
    } catch (e) { setError(e.message); }
  }

  /*
   * NOT NOW — he has seen what the card is asking and is choosing not to do it today.
   *
   * Deliberately NOT the "Ignore" the rest of this system means by that word: that one
   * carries a reason from a fixed vocabulary and tells the lead agent the company is the
   * wrong kind for us. This says nothing about the company, only about today.
   *
   * The card goes quiet until tomorrow, which is the hold treatment it already has — the
   * hatch and "back tomorrow" — so nothing new appears on the board. What is new is the
   * event: the log records work declined rather than done, which is the only way "which
   * cards does he keep putting off" could ever be answered.
   */
  async function skipCompany(company) {
    setMenuFor(null);
    try {
      await api(`/follow-ups/${company.id}/skip`, { method: "POST", body: JSON.stringify({}) });
      await load();
    } catch (e) { setError(e.message); }
  }

  // Take it off hold. `what` is narrowed to the kind the card is actually showing, so
  // lifting a snooze cannot silently clear an ignore that was set separately — the
  // Muted list's Un-mute makes the same distinction.
  async function liftHold(company, kind) {
    setMenuFor(null);
    try {
      await api(`/follow-ups/${company.id}/unmute`, {
        method: "POST",
        body: JSON.stringify({ what: kind === "ignored" ? "ignore" : "snooze" }),
      });
      await load();
    } catch (e) { setError(e.message); }
  }

  // Mark the company a vendor — leaves the board and all reports; timeline kept.
  //
  // Routed through the CONTACT endpoint when there is a POC, because a vendor is
  // a fact about the person too: their card left the board but they stayed in
  // the Leads list and the funnel as a lead who never converted. Same call the
  // Leads page's "Vendor…" makes, so the two surfaces cannot drift — the same
  // reason the junk case above reaches for /contacts/:id/junk.
  async function markVendor(company) {
    setMenuFor(null);
    try {
      if (company.primary_contact_id) {
        await api(`/contacts/${company.primary_contact_id}/vendor`, { method: "POST", body: JSON.stringify({}) });
      } else {
        await api(`/companies/${company.id}`, { method: "PATCH", body: JSON.stringify({ status: "vendor" }) });
      }
      await load();
    } catch (e) { setError(e.message); }
  }

  if (error && !board) return <><Topbar title="Pipeline" /><div className="crm-view"><p className="crm-login__err">{error}</p></div></>;
  if (!board) return <><Topbar title="Pipeline" /><div className="crm-view">Loading…</div></>;

  // Header: companies in play across the OPEN columns. No money on it — a
  // board-wide monthly total says nothing about what to do next, same reason the
  // per-column totals came off the column heads.
  const open = board.columns.filter((c) => OPEN_STAGES.includes(c.stage));
  // Dormant cards are excluded outright, not merely hidden: "companies in play"
  // is a claim about live work, and a lead nobody has heard back from twice is
  // not in play whether or not its column is currently showing it.
  const inPlay = open.reduce(
    (n, c) =>
      n +
      c.companies.filter(
        (co) => !co.dormant && (!hideStale || !(co.stale || co.ghosted))
      ).length,
    0
  );
  // Optimistic, then persisted — the column should disappear on the click, not
  // after a round trip.
  async function toggleColumn(stage) {
    const next = hidden.includes(stage) ? hidden.filter((s) => s !== stage) : [...hidden, stage];
    setHidden(next);
    try {
      await api("/settings", { method: "PUT", body: JSON.stringify({ board_hidden_columns: next }) });
    } catch (e) {
      setHidden(hidden);
      setError(e.message);
    }
  }

  // The columns actually on the board, in order — the render list and, on a
  // phone, the pages the pager counts. "Showing active only" takes Closed lost
  // with it: a lost company is the one thing on the board that is definitionally
  // not active, so leaving the column up contradicts the toggle. Not written to
  // board_hidden_columns — this hide belongs to the toggle and comes back with
  // it, where the − button's hide is a standing choice.
  //
  // An EMPTY column goes too (Gabriel, 2026-09-30): a head with a 0 under it is
  // width the board spends saying nothing. Empty means nothing under the current
  // filters, dormant cards included — a column holding only dormant leads still
  // has something behind its toggle. It is not a setting and not persisted: the
  // column comes back by itself the moment a company lands in it. While a card
  // is being dragged every column is back, so an empty stage is still somewhere
  // to drop it (the phone's move sheet lists every stage regardless).
  // Hot leaves Closed won and lost empty (neither is in play), so the empty-column
  // rule below takes them off without a line of its own.
  const hotOf = (co) => isHot(co, { held: !!holdState(chips[co.id]) });
  const inScopeOf = (col) =>
    col.companies
      .filter((co) => !hideStale || !(co.stale || co.ghosted))
      .filter((co) => !auditedOnly || !!co.audited_at)
      .filter((co) => !hotOnly || hotOf(co));
  const hotCount = open.reduce((n, c) => n + c.companies.filter(hotOf).length, 0);
  const shown = board.columns.filter(
    (col) =>
      !hidden.includes(col.stage) &&
      !(hideStale && col.stage === "closed_lost") &&
      (dragId != null || inScopeOf(col).length > 0)
  );
  // Hiding a column can leave the tracked page past the end of the board until
  // the scroller settles and reports back. Clamp for the read only — page itself
  // stays whatever geometry last said.
  const atPage = Math.min(page, shown.length - 1);

  return (
    <>
      <Topbar
        title="Pipeline"
        sub={hotOnly ? `${hotCount} hot of ${inPlay} in play` : `${inPlay} companies in play`}
        // "deals" is Gabriel's word for the header, from the mockup — the count
        // is companies (v3 has no deals table, one company IS the deal), and the
        // desktop header still says so. Kept on his call.
        subMobile={`${inPlay} deals`}
      >
        <button
          type="button"
          className={`crm-btn crm-btn--secondary crm-hot${hotOnly ? " is-on" : ""}`}
          aria-pressed={hotOnly}
          title={HOT_TITLE}
          onClick={() => setHotOnly(!hotOnly)}
        >
          <span aria-hidden="true">🔥</span> {hotOnly ? `${hotCount} hot` : "Hot"}
        </button>
        {/* The board's view settings. The button says how many filters are
            narrowing the board, so a board with cards missing is never a mystery
            with the panel shut. */}
        <span className="crm-anchor" ref={gearRef}>
          <button
            type="button"
            className={`crm-btn crm-btn--secondary crm-gear${hideStale || auditedOnly ? " is-on" : ""}`}
            aria-expanded={gearOpen}
            aria-label="Board settings"
            title="Board settings"
            onClick={() => setGearOpen(!gearOpen)}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            {(hideStale || auditedOnly) && (
              <span className="crm-gear__count crm-num">{(hideStale ? 1 : 0) + (auditedOnly ? 1 : 0)}</span>
            )}
          </button>
          {gearOpen && (
            <div className="crm-pop crm-pop--gear">
              <h4>Show</h4>
              <label className="crm-check" title="Hide cards tagged stale or ghosted, and the Closed lost column">
                <input type="checkbox" checked={hideStale} onChange={toggleHideStale} />
                <span>Active only</span>
              </label>
              <label className="crm-check" title="Only the companies whose LinkedIn Ads account we have audited">
                <input type="checkbox" checked={auditedOnly} onChange={() => setAuditedOnly(!auditedOnly)} />
                <span>Audited only</span>
              </label>
              <div className="crm-pop__divider">
                <h4>Cards</h4>
                <label className="crm-check" title="The company's country, beside its name">
                  <input type="checkbox" checked={showFlags} onChange={toggleShowFlags} />
                  <span>Country flag</span>
                </label>
                <label className="crm-check" title="The lead score (0-100) under the after-hours automation's current weights">
                  <input type="checkbox" checked={showScore} onChange={toggleShowScore} />
                  <span>Lead score</span>
                </label>
              </div>
            </div>
          )}
        </span>
        {hidden.length > 0 && (
          <button
            className="crm-btn crm-btn--secondary"
            onClick={() => toggleColumn(hidden[hidden.length - 1])}
            title={`Hidden: ${hidden.map((h) => BOARD_STAGE_LABELS[h] || h).join(", ")}`}
          >
            Show {BOARD_STAGE_LABELS[hidden[hidden.length - 1]] || hidden[hidden.length - 1]}
            {hidden.length > 1 ? ` (+${hidden.length - 1})` : ""}
          </button>
        )}
        {/* On a phone the floating "+" is the way in, and two entry points in a
            56px header is one too many. */}
        <button className="crm-btn crm-btn--secondary crm-hide-narrow" onClick={() => navigate("/leads")}>Add lead</button>
      </Topbar>
      {error && <div className="crm-view" style={{ paddingBottom: 0 }}><p className="crm-login__err">{error}</p></div>}

      {/* Where you are in the board. Only rendered on a phone, where one column
          fills the screen and the other six are off it — on desktop the columns
          are their own map. */}
      {isMobile && shown.length > 1 && (
        <div className="crm-board__pager">
          <div className="crm-board__dots">
            {shown.map((col, i) => (
              <button
                key={col.stage}
                type="button"
                className={`crm-board__dot${i === atPage ? " is-on" : ""}`}
                aria-label={BOARD_STAGE_LABELS[col.stage]}
                aria-current={i === atPage ? "true" : undefined}
                onClick={() => goToPage(i)}
              />
            ))}
          </div>
          <span className="crm-board__where crm-num">
            {atPage + 1} of {shown.length} · swipe ↔
          </span>
        </div>
      )}

      {shown.length === 0 && (
        <div className="crm-view"><div className="crm-empty">Nothing on the board under these filters.</div></div>
      )}
      <div className="crm-board" ref={boardRef} onScroll={isMobile ? onBoardScroll : undefined}>
        {shown.map((col) => {
          const closed = col.stage === "closed_won" || col.stage === "closed_lost";
          // What's actually rendered, so the header count can't contradict the
          // column when stale/ghosted cards are hidden.
          /*
           * WHAT MOVED LAST IS AT THE TOP — Gabriel's call, 2026-09-07, replacing
           * the urgency order the column used to take from the action layer.
           *
           * Dated by `last_activity_at`, the SAME field the card prints in its corner:
           * the column's order and the line you read are one fact, and a column
           * ordered by something the cards do not show is a column you cannot check.
           *
           * ONLY WHAT HAS ALREADY HAPPENED COUNTS. `last_activity_at` is a MAX over
           * activities and a booked meeting is stored as one, so a call on the
           * calendar can be newer than anything that has occurred — OSF Digital's
           * newest "activity" is 31 days from now and it took the top of Call
           * scheduled, above a company we actually met yesterday. A date in the
           * future is a fixture, not a thing that moved, so the card falls back to
           * the newest touch that HAS happened: their last mail, ours, or the last
           * meeting that actually took place, all past-bounded in the same query
           * (`meetings` in src/actions/index.js) and all already on the chip the rest
           * of the card reads.
           *
           * WHAT THIS GIVES UP, said plainly because it was load-bearing: the board
           * no longer floats the card with something due to the top of its column,
           * so the board and Today can now lead with different companies. The three
           * card states are what carry urgency here instead — a red border, a hatch
           * or a fade is visible anywhere in a column, which a sort position is not.
           * Nothing is hidden by the change; only the order moves.
           *
           * ACTIVITY IS ANY ACTIVITY, ours included, so a company we chased this
           * morning sits above one that replied last week. That is what the word
           * means and what the card's own line says; the days-silent number is where
           * their silence is measured (client/src/crm/silence.mjs).
           */
          // One clock for the whole column, so two cards cannot be compared against
          // two different "now"s as the loop runs.
          const now = Date.now();
          // Never dated sorts last, rather than sorting as the epoch or as now.
          const ms = (v) => {
            const t = v ? new Date(v).getTime() : NaN;
            return Number.isFinite(t) ? t : -Infinity;
          };
          const activityAt = (co) => {
            const t = ms(co.last_activity_at);
            if (t <= now) return t;
            const c = chips[co.id] || {};
            return Math.max(ms(c.last_inbound_at), ms(c.last_outbound_at), ms(c.last_meeting_at));
          };
          const inScope = inScopeOf(col)
            .sort((a, b) => activityAt(b) - activityAt(a));
          /*
           * Dormant cards come out of the list and go BACK ON THE END when
           * revealed, never interleaved — a lead nobody has heard back from twice is
           * not made recent by our own last email to it, and hiding it was the point.
           */
          const live = inScope.filter((co) => !co.dormant);
          const dormant = inScope.filter((co) => co.dormant);
          const revealed = !!showDormant[col.stage];
          const visible = revealed ? [...live, ...dormant] : live;
          return (
            <div
              key={col.stage}
              className={`crm-col${dropStage === col.stage ? " crm-col--drop" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDropStage(col.stage); }}
              onDragLeave={() => setDropStage((s) => (s === col.stage ? null : s))}
              onDrop={() => onDrop(col.stage)}
            >
              <div className="crm-col__head">
                {/* The head opens Leads on everyone who was EVER in this column,
                    not only the cards on it now — the board answers "where are
                    they", the list answers "who has been here". */}
                <button
                  type="button"
                  className="crm-col__title"
                  title={`Every lead ever in ${BOARD_STAGE_LABELS[col.stage]}`}
                  onClick={(e) => { e.stopPropagation(); navigate(`/leads?ever_stage=${col.stage}`, { state: { from: { to: "/", label: "Pipeline" } } }); }}
                >
                  {BOARD_STAGE_LABELS[col.stage]}
                </button>
                {closed && <span className="crm-col__window">last {board.closed_window_days}d</span>}
                <span className="crm-col__count crm-num">{live.length}</span>
                {/* "New  3 · 1 dormant" — the dormant half is the control, because
                    the number IS the question ("is there anything in there?") and a
                    separate icon would need its own explanation. */}
                {dormant.length > 0 && (
                  <button
                    type="button"
                    className={`crm-col__dormant${revealed ? " is-on" : ""}`}
                    aria-pressed={revealed}
                    title={revealed ? "Hide dormant leads" : `Show ${dormant.length} dormant lead${dormant.length === 1 ? "" : "s"}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDormant((prev) => ({ ...prev, [col.stage]: !prev[col.stage] }));
                    }}
                  >
                    · {dormant.length} dormant
                  </button>
                )}
                {/* Put the column away. Closed lost in particular is reference
                    material most days, not something to look at. */}
                <button
                  type="button"
                  className="crm-col__hide"
                  title={`Hide ${BOARD_STAGE_LABELS[col.stage]}`}
                  aria-label={`Hide ${BOARD_STAGE_LABELS[col.stage]}`}
                  onClick={(e) => { e.stopPropagation(); toggleColumn(col.stage); }}
                >
                  −
                </button>
              </div>
              <div className="crm-col__body">
                {visible.map((co) => {
                  // The POC's stored lead_source. Cards for companies we can't
                  // attribute render no source pill at all — see ui.js.
                  const src = sourcePill(co.poc_lead_source);
                  const flagged = co.stale || co.ghosted;
                  // A booked meeting outranks the last activity on the card's one
                  // timing line: what's next beats what happened. They usually
                  // agree — future-dated meetings ARE the newest activity — but
                  // with two calls booked the last-activity read showed the later
                  // one while the column sorted by the sooner, so the top card
                  // named a date below it.
                  const upcoming = co.next_meeting_at;
                  /*
                   * IS THIS ONE WAITING ON GABRIEL? — the red border, the pulsing
                   * dot, the highlighted stage line and the footer are all this one
                   * answer (client/src/crm/waiting.mjs), which is the card's own top
                   * action and nothing new.
                   *
                   * Never on a closed or dormant card. Closed is history, and a
                   * dormant card was hidden precisely BECAUSE it looked like work —
                   * dressing a revealed one as today's work would undo the whole
                   * point of the reveal being a one-off look.
                   */
                  const waiting = !closed && !co.dormant ? waitingAction(chips[co.id]) : null;
                  /*
                   * ON HOLD — the opposite state, and it cannot collide with the one
                   * above: a suppressed company is served no actions, so `waiting` is
                   * already null for every card this is set on (client/src/crm/hold.mjs).
                   */
                  const hold = !closed ? holdState(chips[co.id]) : null;
                  const actionItems = !closed ? callActionsOf(chips[co.id]) : null;
                  /*
                   * HOW LONG SINCE THEY SAID ANYTHING — the four-tier fade.
                   *
                   * ONE CARD, ONE STATEMENT. The board already draws a company once,
                   * in the queue that scores it highest; the same rule applies to the
                   * three card states, and they rank in the order of what you can do
                   * about them. A hold is a decision Gabriel made and its own hatch
                   * says why the card is quiet, so it wins — eight of the companies
                   * past the ghost threshold are parked on purpose, and fading them
                   * would argue with the parking. Live work wins next: a card with
                   * something due today is not a card dissolving. Only what is left
                   * fades, which is the set the mockup is about.
                   */
                  const silence = !closed && !hold && !waiting
                    ? silenceTier(chips[co.id], thresholds)
                    : null;
                  return (
                    <div
                      key={co.id}
                      className={`crm-bcard${col.stage === "closed_lost" || co.dormant ? " crm-bcard--muted" : ""}${co.dormant ? " crm-bcard--dormant" : ""}${waiting ? " crm-bcard--waiting" : ""}${hold ? " crm-bcard--onhold" : ""}${silence ? ` crm-bcard--${silence.tier}` : ""}`}
                      draggable
                      // Deferred a tick: setting it re-renders the board with
                      // the empty columns back, and Chrome drops a drag whose
                      // DOM changes inside dragstart itself.
                      onDragStart={() => { const id = co.id; setTimeout(() => setDragId(id), 0); }}
                      onDragEnd={() => { setDragId(null); setDropStage(null); }}
                      onTouchStart={() => startPress(co, col.stage)}
                      onTouchMove={endPress}
                      onTouchEnd={endPress}
                      onTouchCancel={endPress}
                      // The detail view is keyed on a contact, so open the
                      // company's POC. /companies/:id has no route — clicking a
                      // card used to do nothing at all. A long-press that opened
                      // the move sheet must not also navigate on release.
                      onClick={() => {
                        if (pressFired.current) { pressFired.current = false; return; }
                        openCompany(co);
                      }}
                    >
                      <div className="crm-bcard__top">
                        <span className="crm-bcard__name">
                          {/* Decorative: the footer below says the same thing in
                              words, which is what a screen reader gets. */}
                          {waiting && <span className="crm-bcard__dot" aria-hidden="true" />}
                          {showFlags && <Flag country={co.country} />}
                          {co.name}
                        </span>
                        {/* THE ONE THING THAT STAYS INKED. It takes the corner from
                            "Nd in stage" rather than sitting beside it: on a card that
                            is going quiet, how long since they spoke is the number you
                            need and how long it has sat in a column is not. Below the
                            stale threshold there is no silence worth reporting, so a
                            warm card keeps the stage age exactly as it was. */}
                        {silence ? (
                          <span className="crm-bcard__silence" title={silenceTitle(silence)}>
                            <b className="crm-num">{silence.days}</b>
                            <span>days silent</span>
                          </span>
                        ) : co.days_in_stage != null && (
                          /* "0d in stage" is the same thing the cadence line stopped
                             saying: a card is read as prose, and nobody says a thing
                             happened zero days ago. On the day it landed here the
                             label is just "today". */
                          <span className="crm-bcard__days crm-num">
                            {co.days_in_stage === 0 ? "today" : `${co.days_in_stage}d in stage`}
                          </span>
                        )}
                      </div>
                      {(co.poc_first_name || co.poc_email) && (
                        <div className="crm-bcard__poc">
                          {contactName({ first_name: co.poc_first_name, last_name: co.poc_last_name, email: co.poc_email })}
                          {co.poc_job_title ? ` · ${co.poc_job_title}` : ""}
                        </div>
                      )}
                      {/* A bare number, which on a card is read as the deal being
                          worth that. It is not: for a prospect it is their ad
                          budget. The card has no room for the word, so the title
                          carries it rather than leaving the number to imply. */}
                      {/* Only when there IS one. moneyK answers "—" for null, so this
                          row used to print an em dash on every card without a budget —
                          on most of the board — and paid a line of card height for it. */}
                      {co.monthly_value != null && (
                        <div className="crm-bcard__value crm-num" title={moneyMeaningTitle(co.status)}>
                          {moneyK(co.monthly_value, co.currency)}/mo
                        </div>
                      )}
                      {/* E: the stage line is the PRIMARY line — what this card
                          needs next. The generic activity read below stays small and
                          gray, because "when did anything last happen" is a lesser
                          question than "what is it waiting on". */}
                      {!closed && <StageLine stage={col.stage} company={co} chip={chips[co.id]} />}
                      <div className="crm-bcard__meta">
                        {/* FIRST, ahead of even "returning": it is the reason the card
                            is shaded, and nothing else on this row is about right now.
                            The note is Gabriel's own ("reconnect after their board
                            meeting"), so it goes on the hover rather than being lost. */}
                        {hold && (
                          <span
                            className="crm-tag crm-tag--onhold"
                            title={[
                              hold.until ? `on hold until ${dateShort(hold.until)}` : "on hold",
                              hold.note,
                            ].filter(Boolean).join(" — ")}
                          >
                            {hold.label}
                          </span>
                        )}
                        {/* WHAT HE SAID ON A CALL HE WOULD DO, not yet ticked
                            (2026-10-26). Its own tag rather than the stage line's
                            one chip, so a reply waiting cannot hide it; ticked off
                            on the record or on Today. */}
                        {actionItems && (
                          <ActionChip action={{ ...actionItems, label: itemsLabel(actionItems) }} title={itemsTitle(actionItems)} />
                        )}
                        {src && <span className={`crm-pill ${src.cls}`}>{src.label}</span>}
                        {/* We audited their LinkedIn account. Beside the source pill
                            because both say how this lead came to know us; the
                            hover carries the date and the start of the notes. */}
                        {co.audited_at && (
                          <span className="crm-tag crm-tag--audited" title={auditTitle(co)}>audited</span>
                        )}
                        {/* The lead score, live under the automation's weights. Black
                            at or above its threshold, gray below; the hover says
                            what each group put in. */}
                        {showScore && co.lead_score && (
                          <span
                            className={`crm-tag crm-tag--score crm-num${co.lead_score.passes ? " is-pass" : ""}`}
                            title={leadScoreTitle(co.lead_score)}
                          >
                            {co.lead_score.score}
                          </span>
                        )}
                        {/* Returning comes first: it identifies the company
                            ("we've had them before"), which changes how the
                            card is read before any freshness tag does. */}
                        {co.returning && (
                          <span className="crm-tag crm-tag--returning" title={returningTitle(co)}>returning</span>
                        )}
                        {/* NOT ON A HELD CARD. Six of the eleven companies on hold today
                            are also stale, which is not news about them: we are the
                            reason they went quiet. A red warning that a parked deal has
                            not moved is the board arguing with a decision Gabriel
                            already made — the same reason a suppressed card is served
                            no chips. The hatch and "back in 10 weeks" say all of it. */}
                        {/* The tier's own word supersedes the stale/ghosted tags —
                            same subject, one clock instead of two, and it is the clock
                            that does not reset when WE write. The old tags stay only
                            for the cards the fade does not touch. */}
                        {silence ? (
                          <>
                            <span className="crm-tag crm-tag--silence">{silence.label}</span>
                            {/* The last tier asks the only question left. It is a real
                                question and not a countdown, because for most of these
                                the sweep will never ask: 21 of the 28 companies past
                                the threshold have never been chased enough for it to
                                fire, so nothing but this would ever raise them. */}
                            {silence.tier === "gone" && (
                              <button
                                type="button"
                                className="crm-bcard__revive"
                                onClick={(e) => { e.stopPropagation(); openRevive(co.id); }}
                              >
                                close or revive?
                              </button>
                            )}
                          </>
                        ) : hold ? null
                          : co.ghosted ? <span className="crm-tag crm-tag--ghosted">ghosted</span>
                            : co.stale ? <span className="crm-tag crm-tag--stale">stale</span> : null}
                        {/* The card has ONE timing line, and the day it comes back
                            outranks both what is next and what last happened — same
                            reading as the booked meeting below, one step further.
                            Only when there IS a day: an ignore with no end and a
                            snooze waiting on their reply have none, and those cards
                            keep their ordinary activity read rather than going blank. */}
                        {hold && hold.until ? (
                          <span className="crm-bcard__act crm-num">{dateShort(hold.until)}</span>
                        ) : upcoming ? (
                          // Never overdue-red: the thing it names hasn't happened
                          // yet. A stale card with a call booked still carries its
                          // stale tag, which is the honest reading of both facts.
                          <span className="crm-bcard__act crm-num">
                            {activityLabel(co.next_meeting_type || "meeting", upcoming)}
                          </span>
                        ) : (co.last_activity_type || co.last_activity_at) && (
                          <span className={`crm-bcard__act crm-num${flagged ? " crm-bcard__act--overdue" : ""}`}>
                            {activityLabel(co.last_activity_type, co.last_activity_at)}
                          </span>
                        )}
                      </div>

                      {/* Last, flush to the card's bottom edge: the verb for the
                          action the chip above named, and how long it has sat
                          there. Only on a card that is waiting on you. */}
                      <WaitingBar action={waiting} onOpen={() => openCompany(co)} />

                      {!closed && (
                        <button
                          className="crm-bcard__menu"
                          onClick={(e) => { e.stopPropagation(); openMenu(co.id); }}
                        >⋯</button>
                      )}
                      {menuFor === co.id && menuMode === "disqualify" && (
                        <div className="crm-pop" onClick={(e) => e.stopPropagation()}>
                          <h4>Disqualify…</h4>
                          <div className="crm-pop__reasons">
                            {DISQUALIFY_REASONS.map(([v, l]) => (
                              <button key={v} className={reason.value === v ? "sel" : ""} onClick={() => setReason({ ...reason, value: v })}>{l}</button>
                            ))}
                          </div>
                          <input placeholder="Note (optional)" value={reason.note} onChange={(e) => setReason({ ...reason, note: e.target.value })} />
                          <div className="crm-pop__actions">
                            <button className="crm-btn crm-btn--secondary crm-btn--danger-hover" onClick={() => disqualify(co)}>Disqualify</button>
                            <button className="crm-pop__cancel" style={{ border: "none", background: "none", cursor: "pointer", fontWeight: 700 }} onClick={() => setMenuFor(null)}>Cancel</button>
                          </div>
                          <div className="crm-pop__divider crm-pop__menu">
                            {/* FIRST, AND ONLY WHEN SOMETHING IS ACTUALLY ASKING.
                                "Not now" answers a card that wants something; on a quiet
                                card it would be a button with nothing to dismiss. No
                                ellipsis and no panel behind it, because the whole value
                                is that it costs one click — a dialog asking him to pick
                                a day is the snooze he is declining to make. */}
                            {(chips[co.id]?.actions || []).length > 0 && (
                              <button className="crm-btn crm-btn--secondary" onClick={() => skipCompany(co)}>
                                Not now
                              </button>
                            )}
                            {/* Above Recycle…, because it is the lighter of the two
                                and the one asked for far more often. A card already
                                on hold offers the way out of it instead. */}
                            <button className="crm-btn crm-btn--secondary" onClick={() => setMenuMode("hold")}>
                              {hold ? "Change hold…" : "Hold…"}
                            </button>
                            <button className="crm-btn crm-btn--secondary" onClick={() => setMenuMode("recycle")}>Remove from pipeline…</button>
                            <button className="crm-btn crm-btn--secondary" onClick={() => setMenuMode("audit")}>
                              {co.audited_at || co.audit_notes ? "Audit notes…" : "Mark audited…"}
                            </button>
                            <button className="crm-btn crm-btn--secondary" onClick={() => markVendor(co)}>Mark as vendor</button>
                          </div>
                        </div>
                      )}
                      {menuFor === co.id && menuMode === "revive" && (
                        <div className="crm-pop" onClick={(e) => e.stopPropagation()}>
                          <h4>{silence ? `Silent ${silence.days} days` : "Gone quiet"}</h4>
                          {/* Revive first. The card has already made the case for
                              closing — this is the option that needs offering, and the
                              composer is one screen away rather than behind a verb
                              this system does not have. */}
                          <div className="crm-pop__menu">
                            <button className="crm-btn crm-btn--secondary" onClick={() => openCompany(co)}>
                              Write to them
                            </button>
                            <button className="crm-btn crm-btn--secondary" onClick={() => setMenuMode("hold")}>
                              Put on hold…
                            </button>
                            <button
                              className="crm-btn crm-btn--secondary crm-btn--danger-hover"
                              onClick={() => closeOut(co)}
                            >
                              Remove from pipeline
                            </button>
                          </div>
                          <p className="crm-hint">
                            Removing keeps the timeline and the history, and reminds you in 6 months.
                            They come back on the board by themselves if they write.
                          </p>
                          <div className="crm-pop__actions">
                            <button
                              className="crm-pop__cancel"
                              style={{ border: "none", background: "none", cursor: "pointer", fontWeight: 700 }}
                              onClick={() => setMenuFor(null)}
                            >Cancel</button>
                          </div>
                        </div>
                      )}
                      {menuFor === co.id && menuMode === "hold" && (
                        <div className="crm-pop" onClick={(e) => e.stopPropagation()}>
                          <h4>Hold until…</h4>
                          <div className="crm-pop__reasons">
                            {Object.keys(SNOOZE_LABELS).map((p) => (
                              <button
                                key={p}
                                className={!holdForm.date && holdForm.preset === p ? "sel" : ""}
                                onClick={() => setHoldForm({ ...holdForm, preset: p, date: "" })}
                              >{SNOOZE_LABELS[p]}</button>
                            ))}
                          </div>
                          {/* Takes over from the chips when set, like the recycle
                              panel's — one of the two decides the day, never both. */}
                          <DateField
                            aria-label="Hold until a specific date"
                            placeholder="Or pick a date"
                            min={todayYmd()}
                            clearable
                            value={holdForm.date}
                            onChange={(d) => setHoldForm({ ...holdForm, date: d })}
                          />
                          {/* Shows on the row when the company wakes up, which is the
                              whole reason to type it: "reconnect after their board
                              meeting" is the thing you will have forgotten by then. */}
                          <input
                            placeholder="Note (optional)"
                            value={holdForm.note}
                            onChange={(e) => setHoldForm({ ...holdForm, note: e.target.value })}
                          />
                          <div className="crm-pop__actions">
                            <button className="crm-btn crm-btn--secondary" onClick={() => holdCompany(co)}>Put on hold</button>
                            <button className="crm-pop__cancel" style={{ border: "none", background: "none", cursor: "pointer", fontWeight: 700 }} onClick={() => setMenuMode("disqualify")}>Back</button>
                          </div>
                          {hold && (
                            <div className="crm-pop__divider crm-pop__menu">
                              <button className="crm-btn crm-btn--secondary" onClick={() => liftHold(co, hold.kind)}>
                                {hold.kind === "ignored" ? "Un-ignore" : "Take off hold"}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      {menuFor === co.id && menuMode === "audit" && (
                        <div className="crm-pop crm-pop--audit" onClick={(e) => e.stopPropagation()}>
                          <h4>LinkedIn audit</h4>
                          <AuditNotes company={co} onChanged={load} compact onDone={() => setMenuFor(null)} />
                        </div>
                      )}
                      {menuFor === co.id && menuMode === "recycle" && (
                        <div className="crm-pop" onClick={(e) => e.stopPropagation()}>
                          <h4>Remove from pipeline…</h4>
                          <div className="crm-pop__reasons">
                            {RECYCLE_REASONS.map(([v, l]) => (
                              <button key={v} className={recycle.reason === v ? "sel" : ""} onClick={() => setRecycle({ ...recycle, reason: v })}>{l}</button>
                            ))}
                          </div>
                          <p className="crm-hint">Remind me in</p>
                          <div className="crm-pop__reasons">
                            {RECYCLE_INTERVALS.map((m) => (
                              <button
                                key={m}
                                className={!recycle.none && !recycle.date && recycle.months === m ? "sel" : ""}
                                onClick={() => setRecycle({ ...recycle, months: m, date: "", none: false })}
                              >{m} months</button>
                            ))}
                            <button
                              className={recycle.none ? "sel" : ""}
                              onClick={() => setRecycle({ ...recycle, date: "", none: true })}
                            >No reminder</button>
                          </div>
                          {/* A custom date takes over from the interval chips, which
                              is why picking one deselects them. */}
                          <DateField
                            aria-label="Custom wake-up date"
                            placeholder="Or pick a date"
                            min={todayYmd()}
                            clearable
                            value={recycle.date}
                            onChange={(d) => setRecycle({ ...recycle, date: d, none: false })}
                          />
                          <div className="crm-pop__actions">
                            <button className="crm-btn crm-btn--secondary" onClick={() => recycleCompany(co)}>Remove</button>
                            <button className="crm-pop__cancel" style={{ border: "none", background: "none", cursor: "pointer", fontWeight: 700 }} onClick={() => setMenuMode("disqualify")}>Back</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {/* Phone only (CSS): the move gesture has no affordance of its
                    own, so the column says it once, at the bottom, where it is
                    out of the way of the cards. */}
                {visible.length > 0 && (
                  <div className="crm-col__hint">Long-press a card to move stage</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {moveFor && (
        <>
          <div className="crm-sheet__backdrop" onClick={() => setMoveFor(null)} />
          <div className="crm-sheet" role="dialog" aria-modal="true" aria-label={`Move ${moveFor.company.name}`}>
            <div className="crm-sheet__grip" aria-hidden="true" />
            <div className="crm-sheet__title">
              Move <b>{moveFor.company.name}</b>{" "}
              <span>from {BOARD_STAGE_LABELS[moveFor.stage]}</span>
            </div>
            {ALL_STAGES.filter((s) => s !== moveFor.stage).map((s) => (
              <button
                key={s}
                type="button"
                className="crm-sheet__item"
                onClick={() => {
                  const id = moveFor.company.id;
                  setMoveFor(null);
                  moveCompany(id, s);
                }}
              >
                {BOARD_STAGE_LABELS[s]}
              </button>
            ))}
            <button type="button" className="crm-sheet__cancel" onClick={() => setMoveFor(null)}>Cancel</button>
          </div>
        </>
      )}
    </>
  );
}
